import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@cropx-common/services/api-gateway/user.state';
import { LocalStorageService } from '@cropx-common/services/local-storage/LocalStorageService';
import { Store } from '@ngxs/store';
import {
  Observable,
  Subject,
  concatMap,
  iif,
  of,
  retryWhen,
  takeUntil,
  throwError,
} from 'rxjs';
import { catchError } from 'rxjs/internal/operators/catchError';
import { delay } from 'rxjs/internal/operators/delay';

import { environment } from '../../../environments/environment';

const API_PATH = environment.api_path;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private injector = inject(Injector);
  private localStorageService = inject(LocalStorageService);
  private store = inject(Store);

  private readonly retries = 1;
  private cancelRequests$ = new Subject<void>();

  private handleError(err: HttpErrorResponse): Observable<any> {
    // The user's login credentials are no longer valid. maybe the password
    // has been changed on mobile. Log the user out and redirect to the login screen
    if (
      (err.status === 403 && err.error.path === '/api/user/permissions') ||
      err.error.path === '/api/jwttoken'
    ) {
      this.localStorageService.clearAll();
      this.store.dispatch(new User.Logout());
      this.injector.get(Router).navigateByUrl(`/login`);
      this.cancelRequests$.next();
    } else if (err.status === 403) {
      this.injector.get(Router).navigateByUrl(`/oops`);
    }
    return throwError(() => err);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      req.url &&
      req.url.includes(API_PATH) &&
      req.headers.get('Authorization') &&
      req.method === 'GET'
    ) {
      const clonedRequest = req.clone({
        headers: req.headers.set(
          'Authorization',
          this.localStorageService.getToken(),
        ),
      });

      return next.handle(clonedRequest).pipe(
        takeUntil(this.cancelRequests$), // Cancel the request if cancelRequests$ emits
        retryWhen((errors) =>
          errors.pipe(
            concatMap((e, i) =>
              iif(
                () => i >= this.retries,
                throwError(() => e),
                of(e).pipe(delay(1000)),
              ),
            ),
          ),
        ),
        catchError((error: HttpErrorResponse) => {
          return this.handleError(error);
        }),
      );
    }
    return next.handle(req).pipe(
      takeUntil(this.cancelRequests$), // Cancel the request if cancelRequests$ emits
    );
  }
}
